import { fetcher, formatLongDate, sanitizeFilename } from '@commenda/utils';
import { API_BASE_PATH } from './constants';

export type ExportServiceRequestFormResponsesParams = {
  serviceRequestId: string;
  /** Exports a single form step; omit to export every form step of the request. */
  taskId?: number;
  entityName?: string | null;
  title?: string | null;
};

const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const filenameFromDisposition = (header: string | null): string | null =>
  header?.match(/filename="([^"]+)"/)?.[1] ?? null;

// Mirrors the BE filename for when Content-Disposition is not exposed to the browser.
const fallbackFilename = (
  entityName?: string | null,
  title?: string | null
): string => {
  const asciiOnly = (part?: string | null): string =>
    sanitizeFilename(part?.replace(/[^\x20-\x7E]/g, ''), '').replace(
      /\s+/g,
      ' '
    );
  const parts = [asciiOnly(entityName), asciiOnly(title)].filter(Boolean);
  return `${[
    ...(parts.length ? parts : ['Service Request']),
    'Form Responses',
    formatLongDate().replace(',', ''),
  ].join(' - ')}.pdf`;
};

const readError = async (response: Response): Promise<string> => {
  const fallback = 'Failed to export form responses';
  try {
    const body: unknown = await response.json();
    if (
      body &&
      typeof body === 'object' &&
      'message' in body &&
      typeof body.message === 'string'
    ) {
      return body.message;
    }
  } catch {
    /* non-JSON body */
  }
  return fallback;
};

export const exportServiceRequestFormResponses = async ({
  serviceRequestId,
  taskId,
  entityName,
  title,
}: ExportServiceRequestFormResponsesParams): Promise<void> => {
  const query = taskId !== undefined ? `?taskId=${taskId}` : '';
  const response = await fetcher(
    `${API_BASE_PATH}/${encodeURIComponent(serviceRequestId)}/form-responses/pdf${query}`,
    { method: 'GET' }
  );
  if (!response.ok) throw new Error(await readError(response));
  triggerBlobDownload(
    await response.blob(),
    filenameFromDisposition(response.headers.get('Content-Disposition')) ??
      fallbackFilename(entityName, title)
  );
};
